import { useState, useEffect } from 'react';
import { Form, Field } from 'react-final-form';
import type { FormRenderProps, FieldRenderProps } from 'react-final-form';
import { noop } from 'lodash';

import TextField from '@app/components/text-field';
import { T } from '@app/components/translation';

import style from './inline-edit.module.scss';

type Props = {
  onEditFolderHandler: (value: string) => void;
  value: string;
  onBlurHandler?: () => void;
  setHasError?: (error: boolean) => void;
};

const InlineEdit = ({ setHasError, onEditFolderHandler, onBlurHandler, value }: Props) => {
  const [editingValue, setEditingValue] = useState(value);
  const [isMounted, setIsMounted] = useState(false);
  const maxChars = 130;
  const currentChars = maxChars - editingValue.length;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Need this because the autoFocus prop loses focus when the input is rendered
  const onRef = (ref: TextField | null) => ref?.inputRef.current?.focus();

  const onBlur = () => {
    // Prevent onBlur from triggering during initial mount
    if (!isMounted) {
      return;
    }

    const trimmedValue = editingValue.trim();

    if (trimmedValue.length > maxChars) {
      return null;
    }

    return onEditFolderHandler(trimmedValue);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === 'Escape') {
      event.stopPropagation();
      onBlurHandler?.();
    }

    if (event.key === 'Enter') {
      const target = event.target as HTMLInputElement;
      target.value = target.value.trim();

      if (target.value.length > maxChars) {
        return null;
      }

      onEditFolderHandler(target.value);
    }

    return null;
  };

  const validateInput = (inputValue: string) => {
    if (inputValue.length > maxChars) {
      setHasError?.(true);
      return (
        <T
          _str="Folder name cannot be longer than 130 characters."
          _comment="Error message for folder name length."
        />
      );
    }

    // Check for unsupported characters: " / \ < > : * | ?
    const unsupportedCharsRegex = /["/\\<>:*|?]/;
    if (unsupportedCharsRegex.test(inputValue)) {
      setHasError?.(true);
      return (
        <T
          _str='Characters " / \  <  >  :  * | ? are not supported.'
          _comment="Error message for unsupported characters in folder name."
        />
      );
    }

    setHasError?.(false);
    return null;
  };

  return (
    <Form
      onSubmit={noop}
      initialValues={{
        folder: value,
      }}
      render={(
        formProps: FormRenderProps<{
          folder: string;
        }>,
      ) => (
        <form
          onSubmit={formProps.handleSubmit}
          className={style.FormContainer}
        >
          <Field
            name="folder"
            validate={validateInput}
            render={(fieldProps: FieldRenderProps<string, HTMLElement>) => (
              <TextField
                ref={onRef}
                input={{
                  ...fieldProps.input,
                  onKeyDown: onKeyDown,
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                    setEditingValue(event.target.value);
                    formProps.form.change('folder', event.target.value);
                  },
                }}
                meta={fieldProps.meta}
                aria-label="folder name"
                onBlur={onBlur}
                customClass={style.InputContainer}
                value={editingValue}
              />
            )}
          />
          <span className={style.Chars}>{currentChars}</span>
        </form>
      )}
    />
  );
};

export default InlineEdit;
